import React, { useState, useCallback, FC, useEffect } from "react";
import { Grid, InputLabel, OutlinedInput } from "@material-ui/core";
import Autosuggest from "react-autosuggest";
import { defaultAddressClient } from "../../clients/AddressClient";
import { useOutlinedInputStyles } from "./LabledTextField";
import { useStyles as useLabeledTextFieldStyles } from "../core/LabledTextField";
import { throttle } from "lodash";

interface Props {
    label: string;
    error?: boolean;
    zipCode: string;
    city: string;
    street?: string | null;
    complete: "zipCode" | "city" | "street";
    validate?: boolean;
    maxLength?: number;
    onChange: (newValue: string) => void;
}

export const AddressCompletionField: FC<Props> = (props: Props) => {
    const labeledTextFieldClasses = useLabeledTextFieldStyles();
    const [completions, setCompletions] = useState([] as string[]);
    const [focus, setFocus] = useState(false);

    const value = props[props.complete];

    const warn = value && !completions.includes(value) && props.validate;
    const outlinedInputClasses = useOutlinedInputStyles({
        state: props.error ? "error" : warn ? "warn" : "default",
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const updateCompletions = useCallback(
        throttle(async (change, props) => {
            if (props.complete === "zipCode" && (props.city.trim() || change.value.trim())) {
                setCompletions(
                    await defaultAddressClient.completeZipCode(change.value, props.city),
                );
            } else if (props.complete === "city" && (props.zipCode.trim() || change.value.trim())) {
                setCompletions(
                    await defaultAddressClient.completeCity(props.zipCode, change.value),
                );
            } else if (props.complete === "street" && (props.zipCode.trim() || props.city.trim())) {
                setCompletions(
                    await defaultAddressClient.completeStreet(
                        props.zipCode,
                        props.city,
                        change.value,
                    ),
                );
            } else {
                setCompletions([]);
            }
        }, 500),
        [],
    );

    useEffect(() => {
        updateCompletions({ value }, props);
    }, [focus, updateCompletions, value, props]);

    return (
        <Grid item>
            <InputLabel
                className={
                    props.error
                        ? labeledTextFieldClasses.labledTextfieldLabelError
                        : labeledTextFieldClasses.labledTextfieldLabel
                }
            >
                {props.label}
            </InputLabel>
            <div style={{ position: "relative" }}>
                <Autosuggest
                    suggestions={completions}
                    onSuggestionsFetchRequested={req => updateCompletions(req, props)}
                    onSuggestionsClearRequested={() => 1 /*setCompletions([])*/}
                    getSuggestionValue={x => x}
                    onSuggestionSelected={() => setFocus(false)}
                    renderSuggestion={suggestion => <div>{suggestion}</div>}
                    highlightFirstSuggestion={true}
                    focusInputOnSuggestionClick={false}
                    alwaysRenderSuggestions={focus}
                    shouldRenderSuggestions={() => focus && !!completions}
                    inputProps={{
                        value: value || "",
                        onChange: (e, change) => props.onChange(change.newValue),
                        onFocus: () => setFocus(true),
                        onBlur: () => setFocus(false),
                        autoComplete: "nope",
                    }}
                    renderInputComponent={inputProps => (
                        <OutlinedInput
                            classes={outlinedInputClasses}
                            name={"buildingAddress.zipCode"}
                            data-testid={"buildingAddress.zipCode"}
                            color={"primary"}
                            inputProps={{
                                ...inputProps,
                                // onChange: x => {
                                //     if (inputProps.onChange) {
                                //         inputProps.onChange(x, {
                                //             method: "type",
                                //             newValue: x.currentTarget.value,
                                //         });
                                //     }
                                // },
                                autoComplete: "nope",
                                maxLength: props.maxLength,
                            }}
                            autoComplete="nope"
                            style={{ width: "100%", flex: 1, padding: 0 }}
                        />
                    )}
                />
            </div>
        </Grid>
    );
};
